/**
 * CheckBook (Çek Defteri) Service
 * Backend API ile iletişim için service layer
 */

import { accountingApi } from '../config/apiClient';
import { ACCOUNTING_API } from '../config/api';

// ========== INTERFACES ==========

export interface CheckBook {
  id: number;
  companyId: string;
  bankAccountId: number;
  subAccountId?: number | null;  // ✅ Alt hesap ID (opsiyonel)
  bankAccount?: BankAccountInfo;
  subAccount?: SubAccountInfo;   // ✅ Alt hesap bilgisi
  serialNumber: string;
  startNumber: number;
  endNumber: number;
  totalLeaves: number;
  usedLeaves: number;
  nextAvailableNumber: number;
  currency: string;
  description?: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  // Computed properties
  remainingLeaves?: number;
  isCompleted?: boolean;
  usagePercentage?: number;
}

export interface BankAccountInfo {
  id: number;
  bankName: string;
  accountNumber: string;
  iban?: string;
  currency: string;
}

export interface SubAccountInfo {
  id: number;
  accountName: string;
  accountNumber: string;
  iban?: string;
  currency: string;
}

export interface CheckBookSummary {
  totalCheckBooks: number;
  activeCheckBooks: number;
  completedCheckBooks: number;
  totalLeaves: number;
  usedLeaves: number;
  remainingLeaves: number;
}

export interface CreateCheckBookRequest {
  companyId: string;
  bankAccountId: number;
  subAccountId?: number | null;  // ✅ Alt hesap ID (opsiyonel)
  serialNumber: string;
  startNumber: number;
  endNumber: number;
  currency?: string;
  description?: string;
}

export interface UpdateCheckBookRequest {
  serialNumber: string;
  description?: string;
  isActive: boolean;
  startNumber?: number;
  endNumber?: number;
}

// ========== SERVICE ==========

export const checkBookService = {
  /**
   * Şirkete ait tüm çek defterlerini getir
   */
  async getByCompany(companyId: string): Promise<CheckBook[]> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_BY_COMPANY);
      return result.data || [];
    } catch (error) {
      console.error('[checkBookService] ❌ Çek defterleri alınamadı:', error);
      throw error;
    }
  },

  /**
   * Şirkete ait aktif çek defterlerini getir
   */
  async getActiveByCompany(companyId: string): Promise<CheckBook[]> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_BY_COMPANY_ACTIVE);
      return result.data || [];
    } catch (error) {
      console.error('[checkBookService] ❌ Aktif çek defterleri alınamadı:', error);
      throw error;
    }
  },

  /**
   * Banka hesabına ait çek defterlerini getir
   */
  async getByBankAccount(bankAccountId: number, companyId: string): Promise<CheckBook[]> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_BY_BANK_ACCOUNT(bankAccountId));
      return result.data || [];
    } catch (error) {
      console.error('[checkBookService] ❌ Banka hesabına ait çek defterleri alınamadı:', error);
      throw error;
    }
  },

  /**
   * Banka hesabına ait kullanılabilir çek defterlerini getir
   */
  async getAvailableByBankAccount(bankAccountId: number, companyId: string): Promise<CheckBook[]> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_BY_BANK_ACCOUNT_AVAILABLE(bankAccountId));
      return result.data || [];
    } catch (error) {
      console.error('[checkBookService] ❌ Kullanılabilir çek defterleri alınamadı:', error);
      throw error;
    }
  },

  /**
   * ID ile çek defteri getir
   */
  async getById(id: number, companyId: string): Promise<CheckBook | null> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_BY_ID(id));
      return result.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      console.error('[checkBookService] ❌ Çek defteri alınamadı:', error);
      throw error;
    }
  },

  /**
   * Çek defteri özeti getir
   */
  async getSummary(companyId: string): Promise<CheckBookSummary> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_SUMMARY);
      return result.data || {
        totalCheckBooks: 0,
        activeCheckBooks: 0,
        completedCheckBooks: 0,
        totalLeaves: 0,
        usedLeaves: 0,
        remainingLeaves: 0,
      };
    } catch (error) {
      console.error('[checkBookService] ❌ Çek defteri özeti alınamadı:', error);
      throw error;
    }
  },

  /**
   * Yeni çek defteri oluştur
   */
  async create(data: CreateCheckBookRequest): Promise<CheckBook> {
    try {
      const { data: result } = await accountingApi.post(ACCOUNTING_API.CHECK_BOOKS, data);
      return result.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Çek defteri oluşturulamadı';
      console.error('[checkBookService] ❌ Çek defteri oluşturulamadı:', error);
      throw new Error(message);
    }
  },

  /**
   * Çek defteri güncelle
   */
  async update(id: number, companyId: string, data: UpdateCheckBookRequest): Promise<CheckBook> {
    try {
      const { data: result } = await accountingApi.post(ACCOUNTING_API.CHECK_BOOK_UPDATE(id), data);
      return result.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Çek defteri güncellenemedi';
      console.error('[checkBookService] ❌ Çek defteri güncellenemedi:', error);
      throw new Error(message);
    }
  },

  /**
   * Çek defteri sil (soft delete)
   */
  async delete(id: number, companyId: string): Promise<boolean> {
    try {
      const { data: result } = await accountingApi.post(ACCOUNTING_API.CHECK_BOOK_DELETE(id));
      return true;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Çek defteri silinemedi';
      console.error('[checkBookService] ❌ Çek defteri silinemedi:', error);
      throw new Error(message);
    }
  },

  /**
   * Sonraki çek numarasını kullan
   */
  async useNextNumber(id: number, companyId: string): Promise<number> {
    try {
      const { data: result } = await accountingApi.post(ACCOUNTING_API.CHECK_BOOK_USE_NEXT_NUMBER(id));
      return result.data.checkNumber;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Çek numarası alınamadı';
      console.error('[checkBookService] ❌ Çek numarası alınamadı:', error);
      throw new Error(message);
    }
  },

  /**
   * Çek numarasının kullanılıp kullanılmadığını kontrol et
   */
  async isNumberUsed(checkBookId: number, checkNumber: number, companyId: string): Promise<boolean> {
    try {
      const { data: result } = await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_IS_NUMBER_USED(checkBookId, checkNumber));
      return result.data.isUsed;
    } catch (error) {
      console.error('[checkBookService] ❌ Çek numarası kontrolü yapılamadı:', error);
      throw error;
    }
  },
};

export default checkBookService;
